import { Box, Tooltip, Typography } from "@mui/material";

import { BotOptionSpec, BotRegistryItem } from "features/adminUsers/adminUsers";
import {
  parseOptions,
  readListOption,
  readMapOption,
  readOption,
} from "utils/additionalOptions";

/** One row of the read-only options view: what Edit job shows as a field. */
export interface BotOptionLine {
  key: string;
  label: string;
  value: string;
  help?: string;
  /** An explicit value is stored on the job; false means default or empty. */
  set: boolean;
  /** Declared by the bot's registry entry; undeclared keys are hand-written. */
  declared: boolean;
  /** Declared as required and nothing is stored. */
  missing: boolean;
}

// Keys the panel renders through dedicated controls (the error-mode switch,
// the SSN side flags the backend derives), never as an option row.
const HIDDEN_OPTION_KEYS = [
  "SKIP_ERROR_EMPLOYEES",
  "IS_SSN_FOR_UPLOAD_ON_PAYCOM",
  "IS_SSN_FOR_UPLOAD_ON_UKG",
  "IS_SSN_FOR_UPLOAD_ON_RIPPLING",
  "IS_FOR_UPLOAD",
];

// Login secrets are masked by key too, not only by their spec: a page whose
// registry is still loading, or that looks the bot up under another kind, shows
// them as hand-written keys. Mirrors SECRET_OPTION_KEYS in bot_options.py.
const SECRET_OPTION_KEYS = ["SECURITY_QUESTIONS"];
const MASKED = "***";

function isSecret(key: string, spec?: BotOptionSpec): boolean {
  return Boolean(spec?.secret) || SECRET_OPTION_KEYS.includes(key.toUpperCase());
}

export function botOptionSpecsFor(
  registry: { results?: BotRegistryItem[] } | BotRegistryItem[] | undefined | null,
  botName: string,
): BotOptionSpec[] {
  const items = Array.isArray(registry) ? registry : registry?.results ?? [];
  const match = items.find((row) => `${row.job_kind}.${row.bot_slug}` === botName);
  return match?.metadata?.options ?? [];
}

/** What an unset option runs at, as the panel words it: "all (default)",
 * "on (default)". Null when the bot declares no default. */
export function defaultHint(spec: BotOptionSpec): string | null {
  if (spec.default == null || String(spec.default) === "") return null;
  if (spec.type === "bool") return `${spec.default ? "on" : "off"} (default)`;
  return `${spec.default} (default)`;
}

function describeSpec(spec: BotOptionSpec, raw: string): BotOptionLine {
  const stored = readOption(raw, spec.key);
  const secret = isSecret(spec.key, spec);
  let set = stored != null && stored.trim() !== "";
  let value = "";
  if (spec.type === "list") {
    const items = readListOption(raw, spec.key);
    set = items.length > 0;
    value = secret && set ? MASKED : items.join(", ");
  } else if (spec.type === "map") {
    const entries = Object.entries(readMapOption(raw, spec.key));
    set = entries.length > 0;
    value = entries.map(([k, v]) => `${k} -> ${secret ? MASKED : v}`).join("; ");
  } else if (spec.type === "bool") {
    if (set) value = (stored ?? "").toLowerCase() === "true" ? "on" : "off";
    else value = defaultHint(spec) ?? "";
  } else if (set) {
    value = secret ? MASKED : (stored ?? "").trim();
  } else {
    value = defaultHint(spec) ?? "";
  }
  const missing = !set && Boolean(spec.required) && !value;
  if (!value) value = missing ? "missing (required)" : "not set";
  return { key: spec.key, label: spec.label, value, help: spec.help, set, declared: true, missing };
}

/** Every option the bot declares, valued from `raw`, then any hand-written key. */
export function summarizeBotOptions(
  specs: BotOptionSpec[],
  raw: string | null | undefined,
  omitKeys: string[] = [],
): BotOptionLine[] {
  const text = raw ?? "";
  const omitted = new Set([...HIDDEN_OPTION_KEYS, ...omitKeys].map((k) => k.toUpperCase()));
  const declared = new Set(specs.map((spec) => spec.key.toUpperCase()));
  const lines = specs
    .filter((spec) => !omitted.has(spec.key.toUpperCase()))
    .map((spec) => describeSpec(spec, text));
  for (const token of parseOptions(text)) {
    const key = token.key.toUpperCase();
    if (!token.key || omitted.has(key) || declared.has(key)) continue;
    lines.push({
      key: token.key,
      label: token.key,
      value: isSecret(key) && token.value ? MASKED : token.value || "(flag)",
      set: true,
      declared: false,
      missing: false,
    });
  }
  return lines;
}

// The required option this check has to let through, or it refuses a form that
// cannot be filled in: DOCS_TYPE is not an ordinary option - the panel owns that
// key through the job's Document type field and the "Document types to delete"
// editor, both of which guard it themselves, which is why it is in every omit
// list here. A required switch is no exception: left alone it stores nothing,
// so BotOptionFields asks On or Off for it.
const PLATFORM_OWNED_OPTION_KEYS = ["DOCS_TYPE"];

/** Labels of the options this bot declares required and the job leaves unset.
 *  A `default` is only a placeholder the form shows - it is never written into
 *  `additional_options` - so a required option is satisfied by an explicit
 *  value and by nothing else. Mirrors `missing_required_options` on the API. */
export function missingRequiredOptions(
  specs: BotOptionSpec[],
  raw: string | null | undefined,
): string[] {
  return specs
    .filter(
      (spec) =>
        spec.required &&
        !PLATFORM_OWNED_OPTION_KEYS.includes(spec.key.toUpperCase()) &&
        // A prefilled option runs at its default when unset: never missing.
        !(spec.prefill && spec.default != null) &&
        !describeSpec(spec, raw ?? "").set,
    )
    .map((spec) => spec.label || spec.key);
}

/** Why a typed value does not fit the option's declared format (or, for a
 * strict_choices option, is not one of its choices), or null.
 * Mirrors `malformed_options` on the API, which refuses the save. */
export function formatProblem(
  spec: BotOptionSpec,
  value: string | null | undefined,
): string | null {
  const typed = (value ?? "").trim();
  if (spec.strict_choices && typed) {
    const choices = spec.choices ?? [];
    return choices.includes(typed.replace(/^["']|["']$/g, ""))
      ? null
      : `Must be one of ${choices.join(", ")}.`;
  }
  if (!spec.format || !typed) return null;
  const match = /^(\d{4})(\d{2})(\d{2})$/.exec(typed);
  if (match) {
    const [year, month, day] = match.slice(1).map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    // Date rolls 20230231 over to March: a real date reads back unchanged.
    if (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    ) {
      return null;
    }
  }
  return `Must be a real date written as ${spec.format}, e.g. 20231101.`;
}

/** One message per option whose typed value does not fit its format. */
export function malformedOptions(
  specs: BotOptionSpec[],
  raw: string | null | undefined,
): string[] {
  return specs.flatMap((spec) => {
    const problem = formatProblem(spec, readOption(raw ?? "", spec.key));
    return problem ? [`${spec.label || spec.key}: ${problem}`] : [];
  });
}

export function botOptionTagText(line: BotOptionLine): string {
  return `${line.label}: ${line.value}`;
}

const LABEL_SX = {
  display: "block",
  lineHeight: 1.2,
  letterSpacing: 0.06,
  fontWeight: 700,
  fontSize: 10,
  color: "text.secondary",
} as const;

export default function BotOptionSummary({
  lines,
  dense = false,
}: {
  lines: BotOptionLine[];
  dense?: boolean;
}) {
  if (!lines.length) return null;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          md: dense ? "repeat(2, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))",
        },
        gap: dense ? 1 : 1.5,
      }}
    >
      {lines.map((line) => {
        const label = (
          <Typography variant="overline" sx={LABEL_SX}>
            {line.label}
          </Typography>
        );
        return (
          <Box key={line.key} sx={{ minWidth: 0 }}>
            {line.help ? (
              <Tooltip title={line.help} placement="top-start">
                <Box sx={{ width: "fit-content", cursor: "help" }}>{label}</Box>
              </Tooltip>
            ) : (
              label
            )}
            <Typography
              variant="body2"
              sx={{
                mt: 0.25,
                overflowWrap: "anywhere",
                fontFamily: line.declared ? undefined : "monospace",
                fontSize: line.declared ? undefined : 12,
                color: line.missing ? "error.main" : line.set ? "text.primary" : "text.secondary",
              }}
            >
              {line.value}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
